import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/lib/utils/classnames';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const combinedRef = React.useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const handleFocus = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (triggerRef.current) {
      const isInvalid = triggerRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(triggerRef.current);
      // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
      const borderRadius = computedStyle.borderRadius || '6px';
      triggerRef.current.style.setProperty('border-radius', borderRadius, 'important');

      // border-color만 변경
      if (!isInvalid) {
        triggerRef.current.style.setProperty('border-color', '#000000', 'important');
      } else {
        triggerRef.current.style.setProperty('border-color', '#ef4444', 'important');
      }

      // Ring/outline 제거
      triggerRef.current.style.setProperty('outline', 'none', 'important');
      triggerRef.current.style.setProperty('outline-width', '0', 'important');
      triggerRef.current.style.setProperty('outline-style', 'none', 'important');
      triggerRef.current.style.setProperty('outline-offset', '0', 'important');
      triggerRef.current.style.setProperty('box-shadow', 'none', 'important');
      triggerRef.current.style.setProperty('--tw-ring-offset-shadow', 'none', 'important');
      triggerRef.current.style.setProperty('--tw-ring-shadow', 'none', 'important');
    }
  }, []);

  const handleBlur = React.useCallback((_: React.FocusEvent<HTMLButtonElement>) => {
    if (triggerRef.current) {
      const isInvalid = triggerRef.current.getAttribute('aria-invalid') === 'true';
      const computedStyle = window.getComputedStyle(triggerRef.current);
      // border-radius를 먼저 명시적으로 유지 (렌더링 문제 방지)
      const borderRadius = computedStyle.borderRadius || '6px';
      triggerRef.current.style.setProperty('border-radius', borderRadius, 'important');

      if (isInvalid) {
        triggerRef.current.style.setProperty('border-color', '#ef4444', 'important');
      } else {
        triggerRef.current.style.removeProperty('border-color');
      }

      // Ring/outline 제거 유지
      triggerRef.current.style.setProperty('outline', 'none', 'important');
      triggerRef.current.style.setProperty('box-shadow', 'none', 'important');
    }
  }, []);

  return (
    <SelectPrimitive.Trigger
      ref={combinedRef}
      className={cn(
        // 기본 스타일
        'flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-[#d9dde0] bg-white px-3 py-2 text-sm text-black',
        // Placeholder 스타일
        'data-[placeholder]:text-[#9ca3af]',
        // Ring/outline 완전히 제거
        'outline-none ring-0 focus:outline-none focus:ring-0',
        // Error 상태
        'aria-invalid:border-red-500',
        // Disabled 상태
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Transition
        'transition-colors',
        '[&>span]:line-clamp-1',
        className,
      )}
      style={{
        outline: 'none',
        boxShadow: 'none',
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-[--radix-select-content-transform-origin]',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
